import { motion, Variants } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Monitor,
  Rocket,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PackageGrid } from "@/components/solutions/PackageGrid";
import { HeroGraphic } from "@/components/home/HeroGraphic";
import { ClientPortalExperience } from "@/components/home/ClientPortalExperience";
import { TrustCredentials } from "@/components/home/TrustCredentials";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
} as Variants;

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

const focuses = [
  {
    icon: Rocket,
    title: "From Idea to Shipped Product",
    description:
      "You bring the vision. I build the full stack, from founder discovery and MVP scope through to a production-ready desktop app with cloud backend, auth, billing, and infrastructure.",
    capabilities: [
      "Founder discovery and MVP scoping",
      "Native desktop client (Electron / Tauri)",
      "Cloud API, database, and infrastructure",
      "Code signing, auto-update, and distribution",
    ],
  },
  {
    icon: Monitor,
    title: "AI-Powered Desktop Apps",
    description:
      "Built for founders turning AI ideas into desktop products: local inference, cloud model orchestration, RAG pipelines, and secure integrations that keep user data protected.",
    capabilities: [
      "AI feature architecture and integration",
      "Local and cloud inference strategies",
      "Secure API design and data handling",
      "Production monitoring and release ops",
    ],
  },
];


export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="relative">
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 hero-premium-bg" />

        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 1400 900"
          fill="none"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon
            points="180,80 360,180 360,380 180,480 0,380 0,180"
            stroke="hsl(30 95% 55%)"
            strokeWidth="0.5"
            opacity="0.04"
          />
          <polygon
            points="1100,30 1280,130 1280,330 1100,430 920,330 920,130"
            stroke="hsl(30 95% 55%)"
            strokeWidth="0.5"
            opacity="0.03"
          />
          <polygon
            points="650,500 830,600 830,800 650,900 470,800 470,600"
            stroke="hsl(45 100% 60%)"
            strokeWidth="0.4"
            opacity="0.025"
          />
          <polygon
            points="480,180 580,240 580,360 480,420 380,360 380,240"
            stroke="hsl(30 95% 55%)"
            strokeWidth="0.3"
            opacity="0.035"
          />

          <path
            d="M0,300 C300,250 500,400 800,200 S1200,350 1400,280"
            stroke="hsl(30 95% 55%)"
            strokeWidth="0.5"
            opacity="0.03"
          />
          <path
            d="M0,600 C400,500 600,650 1000,450 S1300,550 1400,500"
            stroke="hsl(45 100% 60%)"
            strokeWidth="0.4"
            opacity="0.025"
          />
          <path
            d="M0,150 C200,100 450,200 700,120 S1100,180 1400,100"
            stroke="hsl(30 95% 55%)"
            strokeWidth="0.3"
            opacity="0.02"
          />

          {[
            [150, 200], [400, 100], [650, 300], [900, 150], [1150, 250],
            [250, 500], [500, 400], [750, 550], [1000, 400], [1250, 500],
            [200, 350], [700, 200], [1050, 300], [450, 650], [900, 600],
          ].map(([x, y], i) => (
            <circle
              key={`bg-n-${i}`}
              cx={x}
              cy={y}
              r="1.5"
              fill={i % 3 === 0 ? "hsl(45 100% 60%)" : "hsl(30 95% 55%)"}
              opacity={0.06 + (i % 4) * 0.015}
            />
          ))}

          <line x1="150" y1="200" x2="400" y2="100" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.04" />
          <line x1="400" y1="100" x2="650" y2="300" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.03" />
          <line x1="650" y1="300" x2="900" y2="150" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.035" />
          <line x1="900" y1="150" x2="1150" y2="250" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.03" />
          <line x1="250" y1="500" x2="500" y2="400" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.03" />
          <line x1="500" y1="400" x2="750" y2="550" stroke="hsl(45 100% 60%)" strokeWidth="0.3" opacity="0.025" />
          <line x1="650" y1="300" x2="500" y2="400" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.025" />
          <line x1="900" y1="150" x2="1000" y2="400" stroke="hsl(45 100% 60%)" strokeWidth="0.3" opacity="0.02" />
          <line x1="200" y1="350" x2="400" y2="100" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.03" />
          <line x1="700" y1="200" x2="1050" y2="300" stroke="hsl(30 95% 55%)" strokeWidth="0.3" opacity="0.025" />
        </svg>

        <div className="absolute inset-0 grid-background opacity-25" />

        <div className="absolute top-[20%] left-[8%] w-[500px] h-[500px] bg-primary/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-[15%] right-[12%] w-[400px] h-[400px] bg-primary/[0.04] rounded-full blur-[100px]" />
        <div className="absolute top-[45%] left-[45%] w-[300px] h-[300px] bg-accent/[0.03] rounded-full blur-[80px]" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={stagger}
              className="text-center lg:text-left"
            >
              <motion.div variants={fadeInUp}>
                <Badge variant="info" className="mb-6">
                  Now accepting founder-led desktop projects
                </Badge>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight"
              >
                Turn Your Idea Into a{" "}
                <span className="text-gradient">
                  Shipped Desktop Product
                </span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-lg lg:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10"
              >
                For founders with a plan and no tech team, and innovators with
                just a name and an idea. I build your platform, webapp, API,
                infra, and native desktop client, hands-on, inside your own
                AWS account. You own every line of code from day one.
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3"
              >
                <Button
                  size="xl"
                  onClick={() => navigate("/intake?mode=call")}
                  className="group"
                >
                  Book a Discovery Call
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById("pricing")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
                >
                  Browse packages
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block"
            >
              <HeroGraphic />
            </motion.div>
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            document
              .getElementById("services-section")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Scroll to For Founders section"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </motion.div>
        </button>
      </section>

      <TrustCredentials />

      <section id="services-section" className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="info" className="mb-4">
              For Founders
            </Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
              One Engineer,{" "}
              <span className="text-gradient">Your Whole Product</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              No agency handoffs. One senior engineer builds and maintains
              your webapp, cloud backend, infrastructure, and native desktop
              client, working directly inside your own AWS account, so you
              own everything, from first sketch to signed release.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {focuses.map((focus, index) => (
              <motion.div
                key={focus.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover-lift glass-card border-gradient group">
                  <CardContent className="p-8">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <focus.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-display font-semibold text-xl mb-3">
                      {focus.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-5">
                      {focus.description}
                    </p>
                    <ul className="space-y-2">
                      {focus.capabilities.map((cap) => (
                        <li key={cap} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                          {cap}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ClientPortalExperience />

      <section id="pricing" className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="font-display text-3xl font-bold mb-3">
              Pick the Path That Fits{" "}
              <span className="text-gradient">Your Stage</span>
            </h3>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Whether you need a blueprint, an MVP rescue, or a full AI desktop
              launch. Transparent pricing with no hourly surprises.
            </p>
          </motion.div>

          <PackageGrid layout="horizontal" />
        </div>
      </section>
    </div>
  );
}
